// The verdict pack (docs/phase-0/slice5-ux-flows.md §4.3-4.4; docs/phase-0/
// slice5-component-designs.md §3.1, proposed `VerdictPack` - renamed `VerdictPackView`
// here to avoid colliding with the imported `VerdictPack` TYPE from @nightcrew/shared).
// Every claim shows its source+date or a "modeled estimate" label, NEVER a bare figure
// (§0 non-negotiable #4) - the anti-hallucination wall is structural on the wire
// (model/validate.ts's own `.refine`), this component only renders what the wall already
// guarantees. The `insufficient_evidence` (abstain) state reuses `CrewAnswer.tsx`'s own
// icon/heading vocabulary (`AbstainIcon`, "What I checked") - mirrored here (not
// imported: that icon is not exported) rather than a new visual language, per the ux
// spec's explicit instruction (§4.3-4.4).
//
// "Build anyway" and "Drop" are CLIENT-SIDE confirm + navigate actions only, never a call
// to an invented backend endpoint (no route for either exists on `VALIDATE_ROUTES`,
// packages/shared/src/http/validate.ts:31-40): confirming "build anyway" stamps the
// `NotYetValidatedDisclosure` going forward and hands off to the screen's `onBuildAnyway`
// (a plain navigate); confirming "drop" hands off to `onDrop` (a plain navigate back to
// the portfolio). Any server-side verdict write (`none -> unvalidated`) or app-row
// cleanup those actions eventually need is a different lane's contract to land against
// the real routes, not this component's job.
import { useRef, useState } from "react";
import type { EvidencePointer, VerdictPack } from "@nightcrew/shared";
import { EvidenceDrawer } from "./EvidenceDrawer.js";
import { ValidateConfirmDialog } from "./ValidateConfirmDialog.js";
import { validateCopy } from "../copy/validate.js";

type VerdictMember = Extract<VerdictPack, { kind: "verdict" }>;
type VerdictSection = VerdictMember["sections"][number];
type VerdictClaim = VerdictSection["claims"][number];

const SECTION_LABEL_KEY = {
  market_size: "validate.verdict.section.market_size",
  competitors: "validate.verdict.section.competitors",
  demand: "validate.verdict.section.demand",
  financial_plan: "validate.verdict.section.financial_plan",
} as const;

/** Mirrors `CrewAnswer.tsx`'s AbstainIcon exactly (not exported there, duplicated here
 * for the identical visual per the ux spec's own "reuse this vocabulary" instruction). */
const AbstainIcon = (): React.JSX.Element => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <line x1="12" y1="7.5" x2="12" y2="13" />
    <circle cx="12" cy="16.5" r="0.75" fill="currentColor" stroke="none" />
  </svg>
);

const ClaimRow = ({
  claim,
  claimKey,
  isEvidenceOpen,
  onToggleEvidence,
}: {
  readonly claim: VerdictClaim;
  readonly claimKey: string;
  readonly isEvidenceOpen: boolean;
  readonly onToggleEvidence: (claimKey: string) => void;
}): React.JSX.Element => {
  const toggleRef = useRef<HTMLButtonElement>(null);
  const firstEvidence: EvidencePointer | undefined = claim.evidence[0];

  return (
    <li className="verdict-claim">
      <p style={{ margin: 0, fontSize: 14 }}>{claim.statement}</p>
      <div className="verdict-claim-meta row aic gap8 wrap-g mt8">
        {claim.figureKind === "modeled_estimate" ? (
          <span className="chip modeled-estimate">
            {validateCopy("validate.verdict.modeledEstimateTag")}
          </span>
        ) : claim.figureKind === "sourced" && firstEvidence !== undefined ? (
          <span className="chip">
            {validateCopy("validate.verdict.sourceLabel", {
              name: firstEvidence.ref,
              date: new Date(firstEvidence.checkedAt).toLocaleDateString(),
            })}
          </span>
        ) : null}
        {claim.evidence.length > 0 ? (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            ref={toggleRef}
            aria-expanded={isEvidenceOpen}
            onClick={() => {
              onToggleEvidence(claimKey);
            }}
          >
            {validateCopy("validate.verdict.seeEvidenceCta")}
          </button>
        ) : null}
      </div>
      {isEvidenceOpen ? (
        <EvidenceDrawer
          claimStatement={claim.statement}
          evidence={claim.evidence}
          onClose={() => {
            onToggleEvidence(claimKey);
          }}
          triggerRef={toggleRef}
        />
      ) : null}
    </li>
  );
};

type Dialog = "none" | "build_anyway" | "drop";
type AcceptedKind = "passed" | "build_anyway";

export const VerdictPackView = ({
  pack,
  appName,
  canManage,
  onRefine,
  onBuildAnyway,
  onDrop,
  onContinue,
}: {
  readonly pack: VerdictPack;
  readonly appName: string;
  /** Owner/admin gate for the three-path chooser (ux spec §1: viewer sees the identical
   * read surface, every action control disabled + a tooltip reason). */
  readonly canManage: boolean;
  readonly onRefine: () => void;
  /** A plain client-side confirm + navigate (no invented backend call, see this file's
   * own header comment); this component owns the confirm-dialog UI only. */
  readonly onBuildAnyway: () => void;
  readonly onDrop: () => void;
  /** Navigate onward (Brand) once a passed verdict or a confirmed build-anyway is
   * accepted. */
  readonly onContinue: () => void;
}): React.JSX.Element => {
  const [dialog, setDialog] = useState<Dialog>("none");
  const [acceptedKind, setAcceptedKind] = useState<AcceptedKind | null>(null);
  const [openEvidenceKey, setOpenEvidenceKey] = useState<string | null>(null);
  const buildAnywayTriggerRef = useRef<HTMLButtonElement>(null);
  const dropTriggerRef = useRef<HTMLButtonElement>(null);

  const toggleEvidence = (claimKey: string): void => {
    setOpenEvidenceKey((current) => (current === claimKey ? null : claimKey));
  };

  const handleConfirmBuildAnyway = (): void => {
    setDialog("none");
    onBuildAnyway();
    setAcceptedKind("build_anyway");
  };

  const handleConfirmDrop = (): void => {
    setDialog("none");
    onDrop();
  };

  const chooser = (
    <>
      <div className="row gap12 mt16 wrap-g">
        <button type="button" className="btn btn-ghost" onClick={onRefine}>
          {validateCopy("validate.verdict.refineCta")}
        </button>
        <button
          type="button"
          ref={buildAnywayTriggerRef}
          className="btn btn-ghost"
          disabled={!canManage}
          onClick={() => {
            setDialog("build_anyway");
          }}
        >
          {validateCopy("validate.verdict.buildAnywayCta")}
        </button>
        <button
          type="button"
          ref={dropTriggerRef}
          className="btn btn-ghost btn-muted"
          disabled={!canManage}
          onClick={() => {
            setDialog("drop");
          }}
        >
          {validateCopy("validate.verdict.dropCta")}
        </button>
      </div>
      {dialog === "build_anyway" ? (
        <ValidateConfirmDialog
          heading={validateCopy("validate.verdict.buildAnywayCta")}
          body={validateCopy("validate.verdict.buildAnywayConfirmBody", { appName })}
          confirmLabel={validateCopy("validate.verdict.buildAnywayConfirmCta")}
          cancelLabel={validateCopy("validate.verdict.cancelCta")}
          triggerRef={buildAnywayTriggerRef}
          onConfirm={handleConfirmBuildAnyway}
          onCancel={() => {
            setDialog("none");
          }}
        />
      ) : null}
      {dialog === "drop" ? (
        <ValidateConfirmDialog
          heading={validateCopy("validate.verdict.dropCta")}
          body={validateCopy("validate.verdict.dropConfirmBody")}
          confirmLabel={validateCopy("validate.verdict.dropConfirmCta")}
          cancelLabel={validateCopy("validate.verdict.cancelCta")}
          triggerRef={dropTriggerRef}
          onConfirm={handleConfirmDrop}
          onCancel={() => {
            setDialog("none");
          }}
        />
      ) : null}
    </>
  );

  if (acceptedKind !== null) {
    return (
      <div className="card pad" role="status" data-testid="verdict-accepted">
        <p style={{ margin: 0 }}>
          {acceptedKind === "passed"
            ? validateCopy("validate.verdict.continueConfirmPassed")
            : validateCopy("validate.verdict.continueConfirmBuildAnyway", { appName })}
        </p>
        <button type="button" className="btn btn-primary mt12" onClick={onContinue}>
          {validateCopy("validate.verdict.continueButtonCta")}
        </button>
      </div>
    );
  }

  if (pack.kind === "insufficient_evidence") {
    return (
      <div className="card pad" data-testid="verdict-abstain">
        <div className="msg-abstain-flag">
          <AbstainIcon />
          Couldn&apos;t ground this
        </div>
        <p style={{ margin: 0 }}>{validateCopy("validate.verdict.abstainIntro")}</p>
        <h2 className="h3 mt16">{validateCopy("validate.verdict.whatIChecked")}</h2>
        <ul className="stack" style={{ margin: 0, padding: 0, listStyle: "none" }}>
          {pack.gaps.map((gap, index) => (
            <li key={`${gap}-${String(index)}`} className="verdict-claim">
              <p style={{ margin: 0, fontSize: 14 }}>{gap}</p>
            </li>
          ))}
        </ul>
        {chooser}
      </div>
    );
  }

  const isPassed = pack.score === "passed";

  return (
    <div className="card pad verdict-pack-layout" data-testid="verdict-results">
      <div className="verdict-pack-main">
        <p style={{ margin: 0 }}>
          {isPassed
            ? validateCopy("validate.verdict.passedHeadline")
            : validateCopy("validate.verdict.conditionalHeadline")}
        </p>

        {pack.sections.map((section) => (
          <section key={section.kind} className="mt16">
            <h2 className="h3">{validateCopy(SECTION_LABEL_KEY[section.kind])}</h2>
            <p className="muted" style={{ fontSize: 13 }}>
              {section.summary}
            </p>
            <ul className="stack" style={{ margin: 0, padding: 0, listStyle: "none" }}>
              {section.claims.map((claim, index) => {
                const claimKey = `${section.kind}-${String(index)}`;
                return (
                  <ClaimRow
                    key={claimKey}
                    claim={claim}
                    claimKey={claimKey}
                    isEvidenceOpen={openEvidenceKey === claimKey}
                    onToggleEvidence={toggleEvidence}
                  />
                );
              })}
            </ul>
          </section>
        ))}

        {isPassed ? (
          <div className="mt16">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setAcceptedKind("passed");
              }}
            >
              {validateCopy("validate.verdict.continueCta")}
            </button>
          </div>
        ) : (
          chooser
        )}
      </div>
    </div>
  );
};
